import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { debounce } from 'essential-utils';
import React, { useEffect, useRef, useState } from 'react';
export { useMediaQuery };
let ids = [];
export function createId() {
    return `ml-wc-sg-${Math.random().toString(36).slice(2)}`;
}
export function useId() {
    const { current: ownId } = React.useRef(createId());
    return ownId;
}
export function useClickAway(open, onClose) {
    const ownId = useId();
    const ownRef = React.useRef(null);
    React.useEffect(() => {
        if (open) {
            ids.push(ownId);
        }
        else {
            ids = ids.filter((id) => id !== ownId);
        }
    }, [ownId, open]);
    React.useLayoutEffect(() => {
        const onMouseDown = (e) => {
            const ownEl = document.getElementById(ownId);
            const target = e.target;
            if (open && target && ownEl && ownRef.current) {
                if (target === ownRef.current ||
                    ownRef.current.contains(target)) {
                    return;
                }
                if (target === ownEl || ownEl.contains(target)) {
                    return;
                }
                const hasAnyRef = ids.some((activeId) => {
                    const activeRef = document.getElementById(activeId);
                    if (activeRef?.contains(target)) {
                        return true;
                    }
                    return false;
                });
                const otherRef = ids.find((activeId) => {
                    const activeRef = document.getElementById(activeId);
                    if (activeRef?.contains(target) && activeId !== ownId) {
                        return true;
                    }
                    return false;
                });
                if (otherRef) {
                    const ownIndex = ids.indexOf(ownId);
                    const otherIndex = ids.indexOf(otherRef);
                    if (ownIndex < otherIndex) {
                        return;
                    }
                }
                else if (!otherRef && hasAnyRef) {
                    return;
                }
                onClose();
            }
        };
        document.addEventListener('mousedown', onMouseDown);
        return () => document.removeEventListener('mousedown', onMouseDown);
    }, [open, onClose, ownId]);
    return { id: ownId, ref: ownRef };
}
export function useInitialShrink(value, loading = false) {
    const initialShrink = (() => {
        if (Array.isArray(value)) {
            return value.length === 0 ? undefined : true;
        }
        return value ? true : undefined;
    })();
    const { transitions } = useTheme();
    const [shrink, setShrink] = React.useState(initialShrink);
    const shrinkDuration = transitions.duration.shorter + 16;
    React.useEffect(() => {
        if (shrink && !loading) {
            const timeout = setTimeout(() => {
                setShrink(undefined);
            }, shrinkDuration);
            return () => clearTimeout(timeout);
        }
        return;
    }, [shrink, shrinkDuration, loading]);
    return value ? true : shrink;
}
export function useHover(ref) {
    const [hovered, setHovered] = useState(false);
    const [pressed, setPressed] = useState(false);
    useEffect(() => {
        const onMouseEnter = () => {
            setHovered(true);
        };
        const onMouseLeave = () => {
            setHovered(false);
        };
        const onMouseUp = () => {
            setPressed(false);
            ref.current?.removeEventListener('mouseup', onMouseUp);
        };
        const onMouseDown = () => {
            setPressed(true);
            addEventListener('mouseup', onMouseUp);
        };
        ref.current?.addEventListener('mouseenter', onMouseEnter);
        ref.current?.addEventListener('mouseleave', onMouseLeave);
        ref.current?.addEventListener('mousedown', onMouseDown);
        return () => {
            ref.current?.removeEventListener('mouseenter', onMouseEnter);
            ref.current?.removeEventListener('mouseleave', onMouseLeave);
            ref.current?.removeEventListener('mousedown', onMouseDown);
            // eslint-disable-next-line react-hooks/exhaustive-deps
            ref.current?.removeEventListener('mouseup', onMouseUp);
        };
    }, [ref]);
    return { hovered, pressed };
}
export function useMounted() {
    const mounted = useRef(false);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    return mounted;
}
/**
 * TODO: Find a way to replace ml-webclient-common/src/hooks/use-size.ts with this hook
 */
export function useSize(ref) {
    const innerRef = useRef(null);
    const [rect, setRect] = React.useState(undefined);
    const outerRef = ref || innerRef;
    const calc = React.useCallback(() => {
        if (!outerRef.current) {
            return;
        }
        setRect(outerRef.current.getBoundingClientRect());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    React.useEffect(() => {
        if (!outerRef.current) {
            return;
        }
        const debouncedCalc = debounce(calc, 50);
        const rObserver = new ResizeObserver(debouncedCalc);
        const mObserver = new MutationObserver(debouncedCalc);
        rObserver.observe(outerRef.current);
        mObserver.observe(outerRef.current, {
            childList: true
        });
        debouncedCalc();
        return () => {
            mObserver.disconnect();
            rObserver.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outerRef.current]);
    return { ref: outerRef, rect };
}
export function useMergeRefs(ref) {
    const innerRef = React.useRef(null);
    React.useImperativeHandle(ref, () => innerRef.current, []);
    return innerRef;
}
function isOverflow(ref) {
    return (ref.current?.scrollWidth ?? 0) > (ref.current?.clientWidth ?? 0);
}
export function useOverflow(ref, value) {
    const [textOverflow, setTextOverflow] = React.useState(isOverflow(ref));
    const [width, setWidth] = React.useState(ref.current?.clientWidth ?? 0);
    const mounted = useMounted();
    const valueDep = value ?? null;
    React.useEffect(() => {
        const calc = () => {
            if (mounted.current) {
                setTextOverflow(isOverflow(ref));
                setWidth(ref.current?.clientWidth ?? 0);
            }
        };
        if (globalThis.ResizeObserver) {
            const timeout = setTimeout(calc, 0);
            const obs = new ResizeObserver(debounce(calc, 32));
            if (ref.current) {
                obs.observe(ref.current);
            }
            return () => {
                clearTimeout(timeout);
                obs.disconnect();
            };
        }
        return;
    }, [mounted, ref, valueDep, textOverflow, width]);
    return { textOverflow, width };
}
